import { useCallback } from 'react'
import { useNotifications as useNotificationContext } from '@/context/NotificationContext'

/**
 * Hook for notification action methods with enhanced loading states
 *
 * This hook provides all notification actions (mark as read, delete, etc.)
 * with individual loading states and callbacks for UI feedback.
 * Optimistic updates are handled automatically by the underlying context.
 *
 * @example
 * ```typescript
 * function NotificationItem({ notification }) {
 *   const { markAsRead, deleteNotification, loading } = useNotificationActions({
 *     onMarkAsRead: (id) => console.log(`Marked ${id} as read`),
 *     onDelete: (id) => console.log(`Deleted ${id}`),
 *     onError: (error) => toast.error(error)
 *   })
 *
 *   return (
 *     <div>
 *       <button
 *         onClick={() => markAsRead(notification.id)}
 *         disabled={loading.markingAsRead}
 *       >
 *         {loading.markingAsRead ? 'Marking...' : 'Mark as Read'}
 *       </button>
 *       <button
 *         onClick={() => deleteNotification(notification.id)}
 *         disabled={loading.deleting}
 *       >
 *         {loading.deleting ? 'Deleting...' : 'Delete'}
 *       </button>
 *     </div>
 *   )
 * }
 * ```
 */

export interface UseNotificationActionsOptions {
  /** Called when a notification is successfully marked as read */
  onMarkAsRead?: (notificationId: string) => void

  /** Called when all notifications are successfully marked as read */
  onMarkAllAsRead?: (count: number) => void

  /** Called when a notification is successfully deleted */
  onDelete?: (notificationId: string) => void

  /** Called when an action fails */
  onError?: (error: string, action: string, notificationId?: string) => void

  /** Called when any action succeeds */
  onSuccess?: (action: string, notificationId?: string) => void
}

export function useNotificationActions(
  options: UseNotificationActionsOptions = {}
) {
  const context = useNotificationContext()
  const { onMarkAsRead, onMarkAllAsRead, onDelete, onError, onSuccess } =
    options

  /**
   * Mark a notification as read with callback support
   */
  const markAsRead = useCallback(
    async (notificationId: string) => {
      try {
        await context.actions.markAsRead(notificationId)
        onMarkAsRead?.(notificationId)
        onSuccess?.('markAsRead', notificationId)
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to mark as read'
        onError?.(errorMessage, 'markAsRead', notificationId)
        throw error // Re-throw for component error handling
      }
    },
    [context.actions, onMarkAsRead, onSuccess, onError]
  )

  /**
   * Mark all notifications as read with callback support
   */
  const markAllAsRead = useCallback(async () => {
    try {
      await context.actions.markAllAsRead()
      const { unreadCount } = context.state
      onMarkAllAsRead?.(unreadCount)
      onSuccess?.('markAllAsRead')
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to mark all as read'
      onError?.(errorMessage, 'markAllAsRead')
      throw error // Re-throw for component error handling
    }
  }, [context.actions, context.state, onMarkAllAsRead, onSuccess, onError])

  /**
   * Delete a notification with callback support
   */
  const deleteNotification = useCallback(
    async (notificationId: string) => {
      try {
        await context.actions.deleteNotification(notificationId)
        onDelete?.(notificationId)
        onSuccess?.('delete', notificationId)
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to delete notification'
        onError?.(errorMessage, 'delete', notificationId)
        throw error // Re-throw for component error handling
      }
    },
    [context.actions, onDelete, onSuccess, onError]
  )

  return {
    // Action methods
    markAsRead,
    markAllAsRead,
    deleteNotification,

    // Loading states
    loading: {
      markingAsRead: context.state.loading.markingAsRead,
      deleting: context.state.loading.deleting,
      bulkOperating: context.state.loading.bulkOperating,
    },

    // Direct access to refresh for after actions
    refresh: context.actions.refresh,
  }
}

/**
 * Default export for convenience
 */
export default useNotificationActions
